/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpClient, HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Observable, catchError, combineLatest, first, mergeMap, switchMap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { Store } from "@ngrx/store";
import { FromProgram, FromToom, FromUser, RootState, ToomActions, UserActions } from "src/app/store";
import { AccessTokenDto } from "../../store/toom/access-token-dto.interface";

interface KeycloakTokenResponse {
  fault: {
    faultstring: string;
    detail: {
      errorcode: string;
    };
  };
}

@Injectable({ providedIn: "root" })
export class ToomInterceptor implements HttpInterceptor {
  private readonly store = inject(Store<RootState>);
  private readonly httpClient = inject(HttpClient);

  private readonly accessToken$: Observable<string | undefined> = this.store.select(FromUser.selectAccessToken);
  private readonly toomAccessToken$: Observable<string | undefined> = this.store.select(FromToom.selectAccessToken);
  private readonly toomRefreshToken$: Observable<string | undefined> = this.store.select(FromToom.selectRefreshToken);
  private readonly keycloakUrl$ = this.store.select(FromProgram.selectKeycloakUrl) as Observable<string | undefined>;
  private readonly keycloakRealm$ = this.store.select(FromProgram.selectKeycloakRealm) as Observable<string | undefined>;

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isToomApiRequest(req)) return next.handle(req);

    return combineLatest([this.accessToken$, this.toomAccessToken$, this.toomRefreshToken$, this.keycloakUrl$, this.keycloakRealm$]).pipe(
      first(),
      switchMap(([accessToken, toomAccessToken, toomRefreshToken, keycloakUrl, keycloakRealm]) => {
        if (!toomAccessToken) {
          return this.httpClient
            .get<AccessTokenDto>(`${keycloakUrl}/realms/${keycloakRealm}/broker/toom-id/token`, {
              headers: { authorization: `Bearer ${accessToken}` },
            })
            .pipe(
              mergeMap(response => {
                this.store.dispatch(ToomActions.setAccessToken({ accessToken: response.access_token }));
                this.store.dispatch(ToomActions.setRefreshToken({ refreshToken: response.refresh_token }));
                req = req.clone({ setHeaders: { authorization: `Bearer ${response.access_token}` } });
                return next.handle(req);
              }),
              catchError((e: HttpErrorResponse) => throwError(() => e)),
            );
        }

        req = req.clone({ setHeaders: { authorization: `Bearer ${toomAccessToken}` } });

        return next.handle(req).pipe(
          catchError((error: HttpErrorResponse) => {
            if (this.shouldRefreshToken(error)) {
              const data = new URLSearchParams();
              data.append("client_id", "convercus-admin");
              data.append("grant_type", "refresh_token");
              data.append("refresh_token", toomRefreshToken!);

              return this.httpClient
                .post<AccessTokenDto>(`${environment.toomTokenExchange}`, data, {
                  headers: { "Content-Type": "application/x-www-form-urlencoded" },
                })
                .pipe(
                  mergeMap(response => {
                    this.store.dispatch(ToomActions.setAccessToken({ accessToken: response.access_token }));
                    req = req.clone({ setHeaders: { authorization: `Bearer ${response.access_token}` } });
                    return next.handle(req);
                  }),
                  catchError((e: HttpErrorResponse) => {
                    this.store.dispatch(ToomActions.setAccessToken({ accessToken: undefined }));
                    this.store.dispatch(ToomActions.setRefreshToken({ refreshToken: undefined }));
                    if (this.isToomId(accessToken!)) this.store.dispatch(UserActions.keycloakLogout());
                    return throwError(() => e);
                  }),
                );
            } else {
              return throwError(() => error);
            }
          }),
        );
      }),
    );
  }

  private isToomApiRequest(req: HttpRequest<any>): boolean {
    return req.url.includes(environment.toomApi);
  }

  private isToomId(accessToken: string): boolean {
    return !(accessToken && this.store.select(FromUser.selectIsSuperAdmin));
  }

  private shouldRefreshToken(error: HttpErrorResponse): boolean {
    return (
      error.status === HttpStatusCode.Unauthorized.valueOf() ||
      (error as unknown as KeycloakTokenResponse)?.fault?.faultstring?.includes("Token validation failed.")
    );
  }
}
